/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Injectable } from "@angular/core";
import { Observable, of, throwError } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { QPilotService } from "../../../core/services/qpilot.service";
import { SessionService } from "../../../core/services/session.service";
import { MessageService } from "primeng/api";
import { ScheduledOrder } from "../../../core/models/scheduled-order.interface";
import { ScheduledOrderTotals } from "../../../core/models/scheduled-order-totals.interface";
import { ScheduledOrderCoupon } from "../../../core/models/scheduled-order-coupon.interface";
import { ScheduledOrderShippingRateOption } from "../../../core/models/scheduled-order-shipping-rate-option.interface";
import { QPilotAccessToken } from "../../../core/services/qpilot.service";

/**
 * Service for managing scheduled orders create/edit operations
 * Provides high-level business logic and error handling for scheduled order operations
 */
@Injectable({ providedIn: "root" })
export class ScheduledOrderService {
  constructor(
    private qpilotService: QPilotService,
    private sessionService: SessionService,
    private messageService: MessageService,
  ) {}

  /**
   * Create a new scheduled order
   * @param siteId Site ID
   * @param order Scheduled order data
   * @param accessToken Optional access token for embedded mode
   * @returns Observable of created scheduled order
   */
  create(
    siteId: number,
    order: ScheduledOrder,
    accessToken?: QPilotAccessToken,
  ): Observable<ScheduledOrder> {
    const token = accessToken ?? this.sessionService.accessToken;
    if (!token) {
      return throwError(() => new Error("No access token available"));
    }
    const prepared = this.prepareOrderForSave(order);

    return this.qpilotService
      .createScheduledOrder(prepared, token, false)
      .pipe(
        catchError((error) =>
          this.handleError(error, "create scheduled order"),
        ),
      );
  }

  /**
   * Update an existing scheduled order
   * @param siteId Site ID
   * @param order Scheduled order data with Id
   * @param accessToken Optional access token for embedded mode
   * @returns Observable of updated scheduled order
   */
  update(
    siteId: number,
    order: ScheduledOrder,
    accessToken?: QPilotAccessToken,
  ): Observable<ScheduledOrder> {
    if (!order.Id) {
      return throwError(
        () => new Error("Scheduled order ID is required for update"),
      );
    }
    const token = accessToken ?? this.sessionService.accessToken;
    if (!token) {
      return throwError(() => new Error("No access token available"));
    }

    const prepared = this.prepareOrderForSave(order);
    // Enable synchronous pre-validation for updates
    prepared.PreValidateSynchronously = true;

    return this.qpilotService.updateScheduledOrder(prepared, token, false).pipe(
      tap(() => {
        this.messageService.add({
          severity: "success",
          summary: "Success",
          detail: "Scheduled order updated successfully",
          life: 3000,
        });
      }),
      catchError((error) => this.handleError(error, "update scheduled order")),
    );
  }

  /**
   * Delete a scheduled order
   * @param siteId Site ID
   * @param orderId Scheduled order ID
   * @param accessToken Optional access token for embedded mode
   * @returns Observable that completes when order is deleted
   */
  delete(
    siteId: number,
    orderId: number,
    accessToken?: QPilotAccessToken,
  ): Observable<void> {
    const token = accessToken ?? this.sessionService.accessToken;
    if (!token) {
      return throwError(() => new Error("No access token available"));
    }
    return this.qpilotService.deleteScheduledOrder(siteId, orderId, token).pipe(
      tap(() => {
        this.messageService.add({
          severity: "success",
          summary: "Success",
          detail: "Scheduled order deleted successfully",
          life: 3000,
        });
      }),
      catchError((error) => this.handleError(error, "delete scheduled order")),
    );
  }

  /**
   * Activate a scheduled order
   * @param siteId Site ID
   * @param orderId Scheduled order ID
   * @param accessToken Optional access token for embedded mode
   * @returns Observable that completes when order is activated
   */
  activate(
    siteId: number,
    orderId: number,
    accessToken?: QPilotAccessToken,
  ): Observable<void> {
    const token = accessToken ?? this.sessionService.accessToken;
    if (!token) {
      return throwError(() => new Error("No access token available"));
    }
    return this.qpilotService
      .activateScheduledOrder(siteId, orderId, token, true)
      .pipe(
        tap(() => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Scheduled order activated successfully",
            life: 3000,
          });
        }),
        catchError((error) =>
          this.handleError(error, "activate scheduled order"),
        ),
      );
  }

  /**
   * Pause a scheduled order
   * @param siteId Site ID
   * @param orderId Scheduled order ID
   * @param accessToken Optional access token for embedded mode
   * @returns Observable that completes when order is paused
   */
  pause(
    siteId: number,
    orderId: number,
    accessToken?: QPilotAccessToken,
  ): Observable<void> {
    const token = accessToken ?? this.sessionService.accessToken;
    if (!token) {
      return throwError(() => new Error("No access token available"));
    }
    return this.qpilotService
      .pauseScheduledOrder(siteId, orderId, token, true)
      .pipe(
        tap(() => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Scheduled order paused successfully",
            life: 3000,
          });
        }),
        catchError((error) => this.handleError(error, "pause scheduled order")),
      );
  }

  /**
   * Resume a paused scheduled order
   * @param siteId Site ID
   * @param orderId Scheduled order ID
   * @param accessToken Optional access token for embedded mode
   * @returns Observable that completes when order is resumed
   */
  resume(
    siteId: number,
    orderId: number,
    accessToken?: QPilotAccessToken,
  ): Observable<void> {
    const token = accessToken ?? this.sessionService.accessToken;
    if (!token) {
      return throwError(() => new Error("No access token available"));
    }
    return this.qpilotService
      .resumeScheduledOrder(siteId, orderId, token, true)
      .pipe(
        tap(() => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Scheduled order resumed successfully",
            life: 3000,
          });
        }),
        catchError((error) =>
          this.handleError(error, "resume scheduled order"),
        ),
      );
  }

  /**
   * Clone/duplicate a scheduled order
   * @param siteId Site ID
   * @param orderId Scheduled order ID to clone
   * @param accessToken Optional access token for embedded mode
   * @returns Observable of the new cloned scheduled order
   */
  clone(
    siteId: number,
    orderId: number,
    accessToken?: QPilotAccessToken,
  ): Observable<ScheduledOrder> {
    const token = accessToken ?? this.sessionService.accessToken;
    if (!token) {
      return throwError(() => new Error("No access token available"));
    }
    return this.qpilotService
      .cloneScheduledOrder(siteId, orderId, token, true)
      .pipe(
        tap((cloned) => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: `Scheduled order cloned successfully as #${cloned.Id}`,
            life: 3000,
          });
        }),
        catchError((error) => this.handleError(error, "clone scheduled order")),
      );
  }

  /**
   * Get a scheduled order by ID
   * @param id Scheduled order ID
   * @param accessToken Optional access token for embedded mode (uses sessionService.accessToken if not provided)
   * @returns Observable of scheduled order
   * @note Currently uses getScheduledOrder which returns ScheduledOrderListItem.
   *       TODO: Create dedicated getScheduledOrderForEdit endpoint that returns full ScheduledOrder
   */
  getById(
    id: number,
    accessToken?: QPilotAccessToken,
  ): Observable<ScheduledOrder> {
    const token = accessToken ?? this.sessionService.accessToken;
    if (!token) {
      return throwError(() => new Error("No access token available"));
    }
    return this.qpilotService.getScheduledOrderFull(id, token, false).pipe(
      catchError((error) => this.handleError(error, "load scheduled order")),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    ) as any as Observable<ScheduledOrder>;
  }

  /**
   * Validate coupons for a scheduled order
   * @param siteId Site ID
   * @param order Scheduled order data
   * @param accessToken Optional access token for embedded mode
   * @returns Observable of validated coupons
   */
  validateCoupons(
    siteId: number,
    order: ScheduledOrder,
    accessToken?: QPilotAccessToken,
  ): Observable<ScheduledOrderCoupon[]> {
    if (!order.Coupons || order.Coupons.trim() === "") {
      return of([]);
    }
    const token = accessToken ?? this.sessionService.accessToken;
    if (!token) {
      return throwError(() => new Error("No access token available"));
    }

    return this.qpilotService
      .validateScheduledOrderCoupons(siteId, order, token)
      .pipe(
        catchError((error) => {
          console.error("Coupon validation error:", error);
          this.messageService.add({
            severity: "warn",
            summary: "Warning",
            detail: "Failed to validate coupons",
            life: 3000,
          });
          return of([]);
        }),
      );
  }

  /**
   * Calculate totals preview for a scheduled order
   * @param siteId Site ID
   * @param order Scheduled order data
   * @param accessToken Optional access token for embedded mode
   * @returns Observable of calculated totals
   */
  calculateTotals(
    siteId: number,
    order: ScheduledOrder,
    accessToken?: QPilotAccessToken,
  ): Observable<ScheduledOrderTotals> {
    const token = accessToken ?? this.sessionService.accessToken;
    if (!token) {
      return throwError(() => new Error("No access token available"));
    }
    // Transform CouponsHistory to ScheduledOrderCoupons (API expects this field name)
    // Old QMC: ScheduledOrderCoupons: scheduledOrder != null ? scheduledOrder.CouponsHistory : []
    const orderForApi = {
      ...order,
      ScheduledOrderCoupons: order.CouponsHistory || [],
    };

    return this.qpilotService
      .scheduledOrderRecalculatePreview(siteId, orderForApi, token)
      .pipe(
        catchError((error) => {
          console.error("Totals calculation error:", error);
          this.messageService.add({
            severity: "warn",
            summary: "Warning",
            detail: "Failed to calculate totals",
            life: 3000,
          });
          // Return empty totals on error
          return of({
            ItemsTotals: {
              SubTotal: 0,
              DiscountTotal: 0,
              CouponDiscounts: [],
            },
            ShippingTotals: {
              SubTotal: {
                Description: "",
                Amount: 0,
                SubtotalDiscountAppliesToRegularPrice: false,
              },
              CouponDiscounts: [],
              Total: 0,
            },
            TaxTotals: {
              Taxes: [],
              Total: 0,
            },
            SubTotal: 0,
            Total: 0,
          });
        }),
      );
  }

  /**
   * Get shipping rate options for a scheduled order
   * @param siteId Site ID
   * @param order Scheduled order data
   * @param accessToken Optional access token for embedded mode
   * @returns Observable of shipping rate options
   */
  getShippingRateOptions(
    siteId: number,
    order: ScheduledOrder,
    accessToken?: QPilotAccessToken,
  ): Observable<ScheduledOrderShippingRateOption[]> {
    const token = accessToken ?? this.sessionService.accessToken;
    if (!token) {
      return throwError(() => new Error("No access token available"));
    }
    return this.qpilotService
      .getScheduledOrderShippingRateOptions(siteId, order, token)
      .pipe(
        catchError((error) => {
          console.error("Shipping rate options error:", error);
          this.messageService.add({
            severity: "warn",
            summary: "Warning",
            detail: "Failed to load shipping rate options",
            life: 3000,
          });
          return of([]);
        }),
      );
  }

  /**
   * Prepare order data for save (create or update)
   * Cleans UI-only fields and matches old QMC payload structure
   * @param order Scheduled order data
   * @returns Prepared scheduled order
   */
  private prepareOrderForSave(order: ScheduledOrder): ScheduledOrder {
    // Determine if this is CREATE (Id is 0 or undefined) or EDIT (Id > 0)
    const isEditMode = order.Id && order.Id > 0;

    // Clean Customer object to remove displayLabel (added by new QMC UI)
    const cleanCustomer = order.Customer
      ? (() => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { displayLabel, ...customer } = order.Customer as any;
          return customer;
        })()
      : order.Customer;

    const prepared = {
      ...order,
      // Clean Customer object
      Customer: cleanCustomer,
      // Clean ScheduledOrderItems - different structure for CREATE vs EDIT
      // CREATE items: Quantity, Product, ProductId, Price, SalePrice, Cycles, MinCycles, MaxCycles
      // EDIT items: Id, ScheduledOrderId, ProductId, Product, Price, SalePrice, OriginalSalePrice, Quantity, Cycles, MinCycles, MaxCycles, CreatedUtc, UpdatedUtc, Metadata
      ScheduledOrderItems: (order.ScheduledOrderItems || []).map((item) => {
        // Base fields for both CREATE and EDIT
        // CRITICAL: Match old QMC field order exactly (lines 5-51 in "payload from old qmc create.json")
        // Order: Quantity → Product → ProductId → Price → SalePrice
        const baseItem: any = {
          Quantity: item.Quantity, // FIRST field (old QMC line 6)
          Product: item.Product, // SECOND field (old QMC lines 7-48)
          ProductId: item.ProductId, // THIRD field (old QMC line 49)
          Price: item.Price, // FOURTH field (old QMC line 50)
          SalePrice: item.SalePrice, // FIFTH field (old QMC line 51)
        };

        // Add EDIT-only fields if in edit mode
        if (isEditMode) {
          return {
            ...baseItem,
            ...(item.Id && { Id: item.Id }),
            ...(item.ScheduledOrderId && {
              ScheduledOrderId: item.ScheduledOrderId,
            }),
            ...(item.OriginalSalePrice !== undefined && {
              OriginalSalePrice: item.OriginalSalePrice,
            }),
            Cycles: item.Cycles ?? 0, // Default to 0 if null/undefined
            ...(item.MinCycles != null && { MinCycles: item.MinCycles }),
            ...(item.MaxCycles != null && { MaxCycles: item.MaxCycles }),
            ...(item.CreatedUtc && { CreatedUtc: item.CreatedUtc }),
            ...(item.UpdatedUtc && { UpdatedUtc: item.UpdatedUtc }),
            ...(item.Metadata && { Metadata: item.Metadata }),
          };
        }

        // CREATE mode: return base fields + cycles (cycles are supported in both CREATE and EDIT)
        // Default cycles to 0 if not set (0 = infinite cycles in QPilot)
        return {
          ...baseItem,
          Cycles: item.Cycles ?? 0, // Default to 0 if null/undefined
          ...(item.MinCycles != null && { MinCycles: item.MinCycles }),
          ...(item.MaxCycles != null && { MaxCycles: item.MaxCycles }),
        };
      }),
    };

    // Clean up coupons string (remove whitespace)
    if (prepared.Coupons) {
      prepared.Coupons = prepared.Coupons.replace(/\s/g, "");
    }

    // Ensure required fields have defaults
    if (prepared.Status === undefined) {
      prepared.Status = "Paused";
    }

    if (prepared.Cycles === undefined) {
      prepared.Cycles = 0;
    }

    if (prepared.NextOccurrenceOffset === undefined) {
      prepared.NextOccurrenceOffset = 0;
    }

    // CRITICAL: For CREATE mode, use WHITELIST approach to match old QMC exactly
    // Old QMC CREATE only sends specific fields (see "payload from old qmc create.json")
    if (!isEditMode) {
      // Build new object with ONLY the fields old QMC sends
      const createPayload: any = {
        FrequencyType: prepared.FrequencyType,
        Frequency: prepared.Frequency,
        ScheduledOrderItems: prepared.ScheduledOrderItems, // Already cleaned above
        CouponsHistory: prepared.CouponsHistory || [],
        PaymentMethodId: prepared.PaymentMethodId || null,
        PaymentMethod: prepared.PaymentMethod || null,
        CurrencyIso: prepared.CurrencyIso,
        Status: prepared.Status,
        UtcOffset: prepared.UtcOffset,
        CustomerId: prepared.CustomerId,
        Customer: prepared.Customer,
        ShippingFirstName: prepared.ShippingFirstName,
        ShippingLastName: prepared.ShippingLastName,
        ShippingStreet1: prepared.ShippingStreet1,
        ShippingStreet2: prepared.ShippingStreet2,
        ShippingCity: prepared.ShippingCity,
        ShippingPostcode: prepared.ShippingPostcode,
        ShippingState: prepared.ShippingState,
        ShippingCountry: prepared.ShippingCountry,
        // CRITICAL: Match old QMC field order EXACTLY (lines 182-187)
        // Old QMC sends these 6 fields in this exact order:
        Coupons: prepared.Coupons || "", // Line 182
        NextOccurrenceOffset: prepared.NextOccurrenceOffset ?? 0, // Line 183
        Cycles: prepared.Cycles ?? 0, // Line 184
        Name: prepared.Name || "", // Line 185
        Note: prepared.Note || "", // Line 186
        NextOccurrenceUtc: prepared.NextOccurrenceUtc, // Line 187
      };

      console.log(
        "📦 CREATE payload (matches old QMC lines 2-187):",
        createPayload,
      );

      return createPayload;
    }

    // EDIT mode: Use WHITELIST approach (match old QMC EDIT payload exactly)
    // Cast to any to access fields that may not be in TypeScript interface
    const preparedAny = prepared as any;

    const editPayload: any = {
      // CRITICAL: Id is required for EDIT operations
      Id: prepared.Id,

      // Processing cycle info
      LastProcessingCycleId: prepared.LastProcessingCycleId,
      LastProcessingCycle: prepared.LastProcessingCycle,

      // Core order fields
      CustomerId: prepared.CustomerId,
      Customer: prepared.Customer,
      Name: prepared.Name,
      LastOccurrenceUtc: prepared.LastOccurrenceUtc,
      NextOccurrenceUtc: prepared.NextOccurrenceUtc,
      NextOccurrenceOffset: prepared.NextOccurrenceOffset,
      Status: prepared.Status,
      CreatedUtc: prepared.CreatedUtc,
      UpdatedUtc: prepared.UpdatedUtc,

      // Frequency settings
      FrequencyType: prepared.FrequencyType,
      FrequencyDisplayName: preparedAny.FrequencyDisplayName,
      Frequency: prepared.Frequency,
      Cycles: prepared.Cycles,

      // Payment info
      PaymentMethodId: prepared.PaymentMethodId,
      PaymentMethod: prepared.PaymentMethod,
      AuthorizeOnly: preparedAny.AuthorizeOnly,

      // Totals
      CurrencyIso: prepared.CurrencyIso,
      Subtotal: prepared.Subtotal,
      ShippingTotal: prepared.ShippingTotal,
      TaxTotal: prepared.TaxTotal,
      Total: prepared.Total,

      // Shipping info
      ShippingRateName: prepared.ShippingRateName,
      ShippingFirstName: prepared.ShippingFirstName,
      ShippingLastName: prepared.ShippingLastName,
      ShippingStreet1: prepared.ShippingStreet1,
      ShippingStreet2: prepared.ShippingStreet2,
      ShippingCity: prepared.ShippingCity,
      ShippingState: prepared.ShippingState,
      ShippingPostcode: prepared.ShippingPostcode,
      ShippingCountry: prepared.ShippingCountry,
      SiteShippingAddressId: prepared.SiteShippingAddressId,
      ShippingRateOptions: prepared.ShippingRateOptions,
      ShippingRateCalculationErrors: preparedAny.ShippingRateCalculationErrors,
      PreferredShippingRateOption: prepared.PreferredShippingRateOption,

      // Contact info
      PhoneNumber: preparedAny.PhoneNumber,
      Company: preparedAny.Company,

      // Additional fields
      LifetimeValue: prepared.LifetimeValue,
      OriginalExternalId: preparedAny.OriginalExternalId,
      ClientContractId: preparedAny.ClientContractId,
      Note: prepared.Note,

      // Items (already cleaned above)
      ScheduledOrderItems: prepared.ScheduledOrderItems,

      // Delivery and error tracking
      EstimatedDeliveryDate: prepared.EstimatedDeliveryDate,
      ScheduledOrderFailureReason: prepared.ScheduledOrderFailureReason,
      ProcessingErrorCode: prepared.ProcessingErrorCode,
      PreProcessingValidationResultCode:
        preparedAny.PreProcessingValidationResultCode,

      // Metadata and origin
      Origin: preparedAny.Origin,
      Metadata: prepared.Metadata,

      // Coupons
      Coupons: prepared.Coupons,
      CouponsHistory: prepared.CouponsHistory,

      // Order state flags
      Locked: prepared.Locked,
      PreValidateSynchronously: prepared.PreValidateSynchronously,
      LastEditableDate: prepared.LastEditableDate,

      // Timezone and import
      UtcOffset: prepared.UtcOffset,
      ImportTransactionId: preparedAny.ImportTransactionId,

      // Display and dunning
      DisplaySalePrice: prepared.DisplaySalePrice,
      IsInDunning: prepared.IsInDunning,

      // Validation
      ValidationResult: prepared.ValidationResult,

      // Shipping settings
      EnableAutoShippingRate: prepared.EnableAutoShippingRate,
      FreeShipping: prepared.FreeShipping,

      // Coupon details
      ScheduledOrderCoupons: preparedAny.ScheduledOrderCoupons,
    };

    return editPayload;
  }

  /**
   * Handle API errors with user-friendly messages
   * @param error Error object
   * @param operation Operation description
   * @returns Observable error
   */
  private handleError(error: unknown, operation: string): Observable<never> {
    let errorMessage = `Failed to ${operation}`;

    if (error && typeof error === "object") {
      const errorObj = error as {
        error?: { message?: string; Message?: string };
        message?: string;
        Message?: string;
      };

      // Try different possible error message paths
      if (errorObj.error?.message) {
        errorMessage = errorObj.error.message;
      } else if (errorObj.error?.Message) {
        errorMessage = errorObj.error.Message;
      } else if (errorObj.message) {
        errorMessage = errorObj.message;
      } else if (errorObj.Message) {
        errorMessage = errorObj.Message;
      }
    }

    this.messageService.add({
      severity: "error",
      summary: "Error",
      detail: errorMessage,
      life: 5000,
    });

    return throwError(() => new Error(errorMessage));
  }
}
